"use client";

import { useEffect, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wallet,
  Copy,
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  Zap,
  Info,
  ChevronRight,
  ExternalLink,
  Tag,
  XCircle,
  ChevronDown,
  ChevronUp,
  Timer,
  Ban,
  RefreshCcw,
  CreditCard,
  Smartphone,
  Landmark,
  Crown,
  Sparkles,
  Star,
  Check,
} from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { PageTransition } from "@/components/ui-enhanced/PageTransition";
import PublicLayout from "@/components/PublicLayout";
import { Mascot } from "@/components/Mascot";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import api from "@/lib/api";
import { toast } from "sonner";
import { usePageContent } from "@/hooks/usePageContent";
import { useMetadata } from "@/hooks/useMetadata";
import { useAuth } from "@/lib/AuthContext";
import { clsx } from "clsx";

/* -- Copy ------------------------------------- */
export const C = {
  title: "Top Up Your Academic Credits",
  subtitle:
    "Purchase credits securely via Paystack. Promo codes are applied instantly before checkout.",
  choosePlan: "Select a credit package",
  questions: (n: number) => `${n} Questions`,
  perQ: (n: number) => `₦${n.toLocaleString()}`,
  select: "Select",
  selected: "Selected",
  havePromo: "Have a promo_code?",
  apply: "Apply",
  applying: "Applying…",
  clear: "Clear",
  invalid: "Invalid code",
  validDiscount: "Discount applied",
  freeQuestionsBadge: "Free questions will be added after payment",
  proceedToPay: "Proceed to Payment",
  paying: "Redirecting…",
  redeemFree: "Redeem Now",
  redeeming: "Redeeming…",
  redeemed: "Redeemed",
  promoApplied: "Promo applied",
  instructions: "Payment Instructions",
  amount: "Amount to Pay",
  whatNext: "What Happens Next",
  step1: "You will be redirected to Paystack to complete payment.",
  step2: "After successful payment, credits are added instantly.",
  step3:
    "If your promo includes free questions or days, they are applied automatically.",
  noPlan: "Select a plan above to continue.",
  secureCheckout: "Secure Checkout",
  poweredBy: "Powered by Paystack",
  paymentMethods: "Payment Methods",
  card: "Card",
  transfer: "Bank Transfer",
  mobile: "Mobile Money",
  orderSummary: "Order Summary",
  subtotal: "Subtotal",
  discount: "Discount",
  total: "Total",
  free: "FREE",
};

const PLANS = [
  {
    id: "top_up",
    name: "Top-Up — 10 Questions",
    amount: 500,
    credits: 10,
    period: "24h access",
    details: [
      "10 question credits",
      "Expires in 24 hours",
      "No rollover",
      "Stacks on any plan",
    ],
    popular: false,
  },
  {
    id: "exam_pass",
    name: "Exam Pass — 14 Days Unlimited",
    amount: 2000,
    credits: 100,
    period: "14 days",
    details: [
      "15 questions / day",
      "5 questions / hour",
      "25% rollover (max 7)",
      "AI-predicted exam questions",
    ],
    popular: true,
  },
  {
    id: "premium",
    name: "Premium — 30 Days Unlimited",
    amount: 1500,
    credits: 100,
    period: "30 days",
    details: [
      "15 questions / day",
      "5 questions / hour",
      "25% rollover (max 7)",
      "40 PDF downloads / month",
    ],
    popular: false,
  },
];

const PAYMENT_METHODS = [
  {
    id: "card",
    name: "Card",
    icon: CreditCard,
    description: "Visa, Mastercard, Verve",
  },
  {
    id: "transfer",
    name: "Bank Transfer",
    icon: Landmark,
    description: "Direct bank payment",
  },
  {
    id: "mobile",
    name: "Mobile / USSD",
    icon: Smartphone,
    description: "USSD / QR payment",
  },
];

/* -- Types ------------------------------------ */
interface PlanPayload {
  id: string;
  name: string;
  amount: number;
  credits: number;
  period: string;
  details: string[];
  popular: boolean;
}

interface PromoValidation {
  valid: boolean;
  reason?: string;
  discount_type?: string;
  discount_value?: number;
  original_amount?: number;
  discount_amount?: number;
  final_amount?: number;
  benefit_description?: string;
  promo_code_id?: string;
  promo_code?: string;
}

interface InitiateRes {
  checkout_url: string;
  transaction_ref: string;
  monnify_reference: string;
  amount: number;
  original_amount: number;
  credits: number;
  promo_code?: string;
  discount_amount: number;
}

/* -- Helpers ---------------------------------- */
const cn = (...c: any[]) => clsx(...c);
const fmtNaira = (n: number) => `₦${n.toLocaleString()}`;

/**
 * Paystack Nigeria local-card pricing: 1.5% + ₦100, where the flat ₦100 is
 * WAIVED for transactions under ₦2,500, and the total fee is CAPPED at ₦2,000.
 *
 * We "gross up" so that after Paystack deducts its cut we still NET the listed
 * plan price. Solving  gross - fee(gross) = net  for the 1.5%+100 band gives
 * gross = (net + 100) / (1 - 0.015).
 *
 * Previously the raw plan price was charged, so a ₦500 plan actually settled at
 * ~₦492 — this is the "the total is wrong / I see my balance as 500" report.
 */
const PAYSTACK_RATE = 0.015;
const PAYSTACK_FLAT = 100;
const PAYSTACK_FLAT_WAIVER_THRESHOLD = 2500;
const PAYSTACK_FEE_CAP = 2000;

export function grossUpAmount(net: number): number {
  if (!Number.isFinite(net) || net <= 0) return 0;
  // Under the waiver threshold Paystack charges 1.5% only (no flat fee).
  let gross =
    net < PAYSTACK_FLAT_WAIVER_THRESHOLD
      ? net / (1 - PAYSTACK_RATE)
      : (net + PAYSTACK_FLAT) / (1 - PAYSTACK_RATE);
  gross = Math.ceil(gross);
  // Respect the ₦2,000 fee cap: never charge more than net + cap.
  if (gross - net > PAYSTACK_FEE_CAP) gross = net + PAYSTACK_FEE_CAP;
  return gross;
}

export const paystackFeeFor = (net: number) =>
  Math.max(0, grossUpAmount(net) - net);
const normalize = (v: string) => v.trim().toLowerCase();

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export default function PayPage() {
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  const [selected, setSelected] = useState<string | null>(null);
  const [expandedPlan, setExpandedPlan] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoResult, setPromoResult] = useState<PromoValidation | null>(null);
  const [validating, setValidating] = useState(false);
  const [initiating, setInitiating] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<string>("card");

  useEffect(() => {
    let cancelled = false;
    api
      .getTokenBalance()
      .then((b) => {
        if (!cancelled) setBalance(b?.tokens ?? b?.extra_credits ?? 0);
      })
      .catch(() => {
        if (!cancelled) setBalance(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const copy = usePageContent("pay", C);

  useMetadata({ key: "pay" });

  const plan = PLANS.find((p) => p.id === selected) as PlanPayload | undefined;

  const applyPromo = useCallback(async () => {
    if (!selected || !promoCode.trim()) return;
    setValidating(true);
    setPromoResult(null);
    try {
      const res = await api.validatePromo({
        code: promoCode.trim(),
        purchase_type: selected,
        purchase_amount: plan?.amount || 0,
      });
      setPromoResult(res as PromoValidation);
      if (!res.valid) {
        toast.error(res.reason || C.invalid);
      }
    } catch (err) {
      toast.error("Could not validate that promo code. Please try again.");
    } finally {
      setValidating(false);
    }
  }, [selected, promoCode, plan]);

  const clearPromo = useCallback(() => {
    setPromoCode("");
    setPromoResult(null);
  }, []);

  const pay = useCallback(async () => {
    if (!selected) {
      toast.error("Please choose a plan first.");
      return;
    }
    // Block the payment while the session is still being resolved —
    // otherwise a fast click lands before the token is available and the
    // gateway rejects the call with 401.
    if (authLoading) {
      toast.error("Please wait while we verify your session.");
      return;
    }
    if (!user) {
      toast.error("Please sign in to complete checkout.");
      router.replace(`/auth/login?redirect=${encodeURIComponent("/pay")}`);
      return;
    }
    setInitiating(true);
    setPayError(null);
    try {
      const res = await api.initiatePayment(
        selected,
        promoResult?.valid ? promoCode : undefined,
      );
      if (res?.checkout_url) {
        // Full page assign (not SPA nav) so the gateway owns the next document.
        window.location.assign(res.checkout_url);
        return;
      }
      // Gateway accepted the call but gave us nowhere to go.
      const msg =
        "Checkout could not be started (the payment gateway did not return a checkout link). Please try again.";
      setPayError(msg);
      toast.error(msg);
    } catch (err: any) {
      // Surface the REAL backend reason. Previously this was swallowed behind
      // "UPLINK ERROR", which is why checkout looked like it did nothing.
      const raw =
        err?.message ||
        (typeof err === "string" ? err : "") ||
        (err?.response?.data?.detail
          ? JSON.stringify(err.response.data.detail)
          : "") ||
        "Unknown error";
      console.error("[pay] initiatePayment_failed:", err);
      const msg = `Payment could_not_be_started: ${raw}`;
      setPayError(msg);
      toast.error(msg);
    } finally {
      setInitiating(false);
    }
  }, [selected, promoResult, promoCode, user, authLoading, router]);

  const redeemFree = useCallback(async () => {
    if (!promoResult?.valid || !promoResult.promo_code) return;
    setRedeeming(true);
    try {
      await api.redeemPromo({
        code: promoResult.promo_code,
        purchase_type: selected || "standalone",
        purchase_amount_original: promoResult.original_amount || 0,
        purchase_amount_discounted: promoResult.final_amount || 0,
      });
      toast.success("Free questions credited to your account.");
      setPromoResult({
        ...promoResult,
        valid: false,
        reason: "Already redeemed",
      });
      setPromoCode("");
    } catch (err) {
      toast.error("Could not redeem that code. Please try again.");
    } finally {
      setRedeeming(false);
    }
  }, [promoResult, selected]);

  const amountToShow =
    promoResult?.valid && selected
      ? (promoResult.final_amount ?? plan?.amount ?? 0)
      : (plan?.amount ?? 0);
  const originalAmount = plan?.amount ?? 0;
  const discountAmount = promoResult?.valid
    ? (promoResult.discount_amount ?? 0)
    : 0;
  const isFreeStandalone =
    promoResult?.valid &&
    promoResult.discount_type === "free_questions" &&
    !selected;

  return (
    <PageTransition>
      <PublicLayout>
        <div className="max-w-6xl mx-auto space-y-8 pb-32 md:pb-24 overflow-x-hidden">
          {/* Hero Header */}
          <div className="relative overflow-hidden rounded-[2.5rem] border-2 border-border bg-gradient-to-br from-y/5 via-background to-background p-8 md:p-12">
            <div className="absolute top-0 right-0 w-64 h-64 bg-y/5 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-y/10 rounded-full blur-2xl" />
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex items-center gap-5">
                <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-3xl bg-y/10 flex items-center justify-center border border-y/20 shadow-[0_0_30px_rgba(255,214,0,0.15)]">
                  <div
                    className="absolute top-0 left-0 w-6 h-6 md:w-7 md:h-7 bg-y opacity-90"
                    style={{ clipPath: "polygon(0 0, 100% 0, 0 100%)" }}
                  />
                  <Wallet
                    size={32}
                    className="text-y relative z-10 md:w-10 md:h-10"
                    strokeWidth={2.5}
                  />
                </div>
                <div>
                  <h1 className="display text-3xl md:text-4xl tracking-tighter uppercase">
                    {copy.title}
                  </h1>
                  <p className="text-muted-foreground text-sm uppercase tracking-widest font-medium italic">
                    {copy.subtitle}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="px-4 py-2 rounded-2xl bg-y/10 border border-y/20 text-y">
                  <p className="text-xs font-black uppercase tracking-widest">
                    {C.secureCheckout}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 px-4 pt-8">
            {/* Left Column - Plans & Promo */}
            <div className="lg:col-span-2 space-y-8">
              {/* Plan Selection */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-y/10 flex items-center justify-center text-y">
                    <Crown size={16} />
                  </div>
                  <h2 className="display text-xl uppercase tracking-tight">
                    {copy.choosePlan}
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {PLANS.map((p) => {
                    const isSelected = selected === p.id;
                    const isExpanded = expandedPlan === p.id;
                    const displayAmount =
                      isSelected && promoResult?.valid
                        ? (promoResult.final_amount ?? p.amount)
                        : p.amount;
                    return (
                      <motion.div
                        key={p.id}
                        layout
                        className={cn(
                          "relative rounded-3xl border-2 transition-all bg-card",
                          isSelected
                            ? "border-y bg-y/5 shadow-[0_0_40px_rgba(255,214,0,0.1)]"
                            : "border-border hover:border-y/40",
                        )}
                      >
                        {p.popular && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                            <Badge
                              variant="y"
                              className="text-[10px] font-black uppercase tracking-widest"
                            >
                              <Star size={10} className="mr-1" /> Popular
                            </Badge>
                          </div>
                        )}
                        <button
                          onClick={() => {
                            setSelected(p.id);
                            setPromoResult(null);
                            setPromoCode("");
                            setExpandedPlan((prev) =>
                              prev === p.id ? null : p.id,
                            );
                          }}
                          disabled={initiating}
                          className="w-full text-left p-5"
                        >
                          {isSelected && (
                            <Badge
                              variant="y"
                              className="absolute -top-2.5 right-3 text-xs font-black uppercase"
                            >
                              {copy.selected}
                            </Badge>
                          )}
                          <div className="space-y-2">
                            <div className="text-xs font-black text-muted-foreground uppercase tracking-widest">
                              {p.name}
                            </div>
                            <div className="display text-2xl text-y leading-none">
                              {fmtNaira(displayAmount)}
                            </div>
                            <div className="text-xs font-bold text-muted-foreground">
                              {copy.questions(p.credits)} · {p.period}
                            </div>
                          </div>
                          <div className="mt-3 flex items-center justify-between gap-1.5 text-xs font-black uppercase tracking-wider text-y">
                            {isSelected ? copy.selected : copy.select}
                            {isExpanded ? (
                              <ChevronUp size={12} />
                            ) : (
                              <ChevronDown size={12} />
                            )}
                          </div>
                        </button>

                        <AnimatePresence initial={false}>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2, ease: "easeInOut" }}
                              className="overflow-hidden"
                            >
                              <div className="px-5 pb-5 pt-0 space-y-2">
                                <div className="border-t border-border/60 pt-3 space-y-2">
                                  {p.details.map((detail) => (
                                    <div
                                      key={detail}
                                      className="flex items-start gap-2 text-xs font-semibold text-foreground/80"
                                    >
                                      <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-y shrink-0" />
                                      <span>{detail}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Promo Code */}
              <Card className="border-2 border-border bg-card/80">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                    <Tag size={14} className="text-y" />
                    {copy.havePromo}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                      placeholder="Enter promo code"
                      className="bg-background/60 border-border/60 font-mono text-xs uppercase"
                    />
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!selected || validating || !promoCode.trim()}
                      onClick={applyPromo}
                      className="text-xs font-black uppercase tracking-wider w-full sm:w-auto shrink-0"
                    >
                      {validating ? copy.applying : copy.apply}
                    </Button>
                  </div>

                  <AnimatePresence>
                    {promoResult && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        className="space-y-2"
                      >
                        {promoResult.valid ? (
                          <div className="p-4 rounded-2xl bg-green-500/10 border border-green-500/20 flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-black text-green-400 uppercase tracking-wider">
                                {copy.promoApplied}
                              </p>
                              <p className="text-xs text-green-300 font-semibold">
                                {promoResult.benefit_description}
                              </p>
                            </div>
                            <button
                              onClick={clearPromo}
                              className="text-muted-foreground hover:text-foreground transition-colors"
                            >
                              <XCircle size={16} />
                            </button>
                          </div>
                        ) : (
                          <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center gap-3">
                            <XCircle className="w-5 h-5 text-red-500 shrink-0" />
                            <p className="text-xs font-semibold text-red-300">
                              {promoResult.reason || C.invalid}
                            </p>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {isFreeStandalone && (
                    <Button
                      className="w-full bg-green-600 text-white hover:bg-green-600/90 font-black uppercase tracking-wider text-xs"
                      disabled={redeeming}
                      onClick={redeemFree}
                    >
                      {redeeming ? copy.redeeming : copy.redeemFree}
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Payment Methods (chosen securely on the Paystack checkout) */}
              <Card className="border-2 border-border bg-card/80">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                    <CreditCard size={14} className="text-y" />
                    {C.paymentMethods}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {PAYMENT_METHODS.map((method) => {
                      const Icon = method.icon;
                      const isSelected = selectedMethod === method.id;
                      return (
                        <button
                          type="button"
                          key={method.id}
                          onClick={() => setSelectedMethod(method.id)}
                          className={`p-4 rounded-2xl border text-center space-y-2 transition-all ${
                            isSelected
                              ? "border-y bg-y/5 shadow-[0_0_24px_rgba(255,214,0,0.12)]"
                              : "border-border bg-card hover:border-y/40"
                          }`}
                        >
                          <Icon size={24} className="mx-auto text-y" />
                          <p className="text-xs font-black uppercase tracking-wider">
                            {method.name}
                          </p>
                          <p className="text-[10px] text-muted-foreground font-medium">
                            {method.description}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-3 text-center text-[10px] text-muted-foreground">
                    Pick your preferred method on the secure Paystack checkout.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Order Summary */}
            <div className="space-y-6">
              <Card className="border-2 border-y/20 bg-y/5 sticky top-4">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-xs uppercase tracking-widest text-y">
                    <Sparkles size={14} />
                    {C.orderSummary}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {plan ? (
                    <>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-black text-muted-foreground uppercase tracking-widest">
                            Plan
                          </span>
                          <span className="text-sm font-bold text-foreground">
                            {plan.name}
                          </span>
                        </div>
                        {balance !== null && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-black text-muted-foreground uppercase tracking-widest">
                              Your balance
                            </span>
                            <span className="text-sm font-bold text-foreground">
                              {balance} credits
                            </span>
                          </div>
                        )}
                        <Separator className="bg-border/50" />
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-black text-muted-foreground uppercase tracking-widest">
                            {C.subtotal}
                          </span>
                          <span className="text-sm font-bold text-foreground">
                            {fmtNaira(originalAmount)}
                          </span>
                        </div>
                        {discountAmount > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-black text-muted-foreground uppercase tracking-widest">
                              {C.discount}
                            </span>
                            <span className="text-sm font-bold text-green-400">
                              -{fmtNaira(discountAmount)}
                            </span>
                          </div>
                        )}
                        <Separator className="bg-border/50" />
                        {amountToShow > 0 &&
                          paystackFeeFor(amountToShow) > 0 && (
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-black text-muted-foreground uppercase tracking-widest min-w-0 break-words">
                                Processing fee
                              </span>
                              <span className="text-sm font-bold text-foreground shrink-0">
                                {fmtNaira(paystackFeeFor(amountToShow))}
                              </span>
                            </div>
                          )}
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-black text-muted-foreground uppercase tracking-widest">
                            {C.total}
                          </span>
                          <span className="display text-xl text-y">
                            {amountToShow === 0
                              ? C.free
                              : fmtNaira(grossUpAmount(amountToShow))}
                          </span>
                        </div>
                        {amountToShow > 0 && (
                          <p className="text-[10px] leading-relaxed text-muted-foreground break-words">
                            Includes the payment processor&apos;s charge so your
                            plan credits the full {fmtNaira(amountToShow)}.
                          </p>
                        )}
                      </div>

                      {payError && (
                        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-2">
                          <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                          <p className="text-xs text-red-400 min-w-0 break-words">
                            {payError}
                          </p>
                        </div>
                      )}

                      <Button
                        onClick={pay}
                        disabled={initiating}
                        className="w-full bg-y text-black hover:bg-y/90 font-black uppercase tracking-wider text-xs py-6 rounded-2xl shadow-[0_0_30px_rgba(255,214,0,0.2)]"
                      >
                        {initiating ? (
                          <>
                            <Timer className="w-4 h-4 animate-spin mr-2" />{" "}
                            {copy.paying}
                          </>
                        ) : (
                          <>
                            <ExternalLink className="w-3.5 h-3.5 mr-1.5" />{" "}
                            {copy.proceedToPay}
                          </>
                        )}
                      </Button>

                      <div className="flex items-center justify-center gap-2 text-[10px] text-muted-foreground font-medium">
                        <ShieldCheck size={12} className="text-y" />
                        {C.poweredBy}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <Wallet className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
                      <p className="text-xs text-muted-foreground">
                        {copy.noPlan}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* What Happens Next */}
              <Card className="border-2 border-border bg-card/80">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                    <Zap size={14} className="text-y" />
                    {copy.whatNext}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {[copy.step1, copy.step2, copy.step3].map((step, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-3 text-xs text-foreground/70"
                      >
                        <span className="shrink-0 w-6 h-6 rounded-lg bg-y/10 flex items-center justify-center text-xs font-black text-y">
                          {i + 1}
                        </span>
                        {step}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </PublicLayout>
    </PageTransition>
  );
}
